// src/pages/DevHub/MockApi/MockApiPage.tsx
import React, { useState, useRef, useEffect } from 'react';
import {
    Terminal,
    Play,
    AlertCircle,
    FileCode,
    Settings2,
    Info,
    Link,
    Braces
} from 'lucide-react';
import SEO from '../../../components/SEO/SEO';
import {
    DevPageLayout,
    DevPanel,
    DevButton,
    DevStatusPill,
    DevAlert,
    DevCodeBlock,
    DevCodeBlockPre,
    DevTerminalWindow,
    DevUrlPreview,
} from '../components';

interface Endpoint {
    url: string;
    method: 'GET' | 'POST';
    description: string;
    payload?: any;
    isBinary?: boolean;
}

interface ApiResponse {
    status: number | string;
    body?: any;
    isBinary?: boolean;
    blobUrl?: string;
    fileName?: string;
    type?: string;
}

type ActiveTab = 'posts' | 'users' | 'products' | 'upload' | 'pdf' | 'image';
type SnippetTab = 'javascript-fetch' | 'javascript-axios' | 'python-requests' | 'node' | 'php' | 'curl';

const endpoints: Record<ActiveTab, Endpoint> = {
    posts: {
        url: '/api/mock-api/posts',
        method: 'GET',
        description: 'Retrieve 10 mock blog posts with realistic titles and metadata to test feed rendering and pagination UI.',
    },
    users: {
        url: '/api/mock-api/users',
        method: 'GET',
        description: 'Access profile data for 10 simulated developers, including avatars and roles, perfect for dashboard prototyping.',
    },
    products: {
        url: '/api/mock-api/products',
        method: 'GET',
        description: 'Fetch 12 mock e-commerce items complete with image URLs and pricing to validate storefront layouts.',
    },
    upload: {
        url: '/api/mock-api/file/upload',
        method: 'POST',
        description: 'Test file intake logic. Supports multi-part or JSON requests; echoes metadata back without persistent storage.',
        payload: { filename: 'report.pdf', type: 'application/pdf', size: 245600 }
    },
    pdf: {
        url: '/api/mock-api/file/pdf',
        method: 'GET',
        isBinary: true,
        description: 'Generate and stream a valid single-page PDF document to validate download and viewer integrations.',
    },
    image: {
        url: '/api/mock-api/file/image',
        method: 'GET',
        isBinary: true,
        description: 'Fetch a branded JPG asset to verify image loading states, lazy loading, and UI-based transformations.',
    }
};

const MockApiPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState<ActiveTab>('posts');
    const [snippetTab, setSnippetTab] = useState<SnippetTab>('javascript-fetch');
    const [loading, setLoading] = useState<boolean>(false);
    const [response, setResponse] = useState<ApiResponse | null>(null);
    const [delay, setDelay] = useState<number>(0);
    const [status, setStatus] = useState<number>(200);
    const playgroundRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (playgroundRef.current) {
            playgroundRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [activeTab]);

    const getFullUrl = (withChaos: boolean = true) => {
        const baseUrl = (import.meta as any).env.VITE_API_URL || 'http://localhost:8000';
        const endpoint = endpoints[activeTab];
        return withChaos
            ? `${baseUrl}${endpoint.url}?delay=${delay}&status=${status}`
            : `${baseUrl}${endpoint.url}`;
    };

    const generateSnippet = (lang: SnippetTab) => {
        const url = getFullUrl();
        const endpoint = endpoints[activeTab];
        const isPost = endpoint.method === 'POST';
        const isBinary = endpoint.isBinary;

        const snippets: Record<SnippetTab, string> = {
            'javascript-fetch': `// Standard Fetch Implementation
fetch('${url}', {
  method: '${endpoint.method}',
  headers: { 'Content-Type': 'application/json' }${isPost ? `,\n  body: JSON.stringify(${JSON.stringify(endpoint.payload, null, 2).replace(/\n/g, '\n  ')})` : ''}
})
.then(response => ${isBinary ? 'response.blob()' : 'response.json()'})
.then(data => {
  ${isBinary ? '// Handle binary stream\n  console.log("File received:", data.size, "bytes");' : 'console.log(data);'}
})
.catch(error => console.error('Error:', error));`,

            'javascript-axios': `// Axios library Implementation
axios({
  method: '${endpoint.method}',
  url: '${url}'${isPost ? `,\n  data: ${JSON.stringify(endpoint.payload, null, 2).replace(/\n/g, '\n  ')}` : ''}${isBinary ? ',\n  responseType: \'blob\'' : ''}
})
.then(response => {
  ${isBinary ? 'console.log("File received:", response.data.size, "bytes");' : 'console.log(response.data);'}
})
.catch(error => {
  console.error('Error:', error);
});`,

            'python-requests': `import requests

# This script will download and save the file
url = "${url}"
${isPost ? `payload = ${JSON.stringify(endpoint.payload, null, 4)}
response = requests.post(url, json=payload)` : 'response = requests.get(url)'}

print(f"Status: {response.status_code}")
${isBinary ? `# Saving binary file as ${activeTab === 'pdf' ? 'mock.pdf' : 'mock.jpg'}\nwith open("${activeTab === 'pdf' ? 'mock.pdf' : 'mock.jpg'}", "wb") as f:\n    f.write(response.content)\nprint("Download Complete!")` : 'print(response.json())'}`,

            'node': `const fetch = require('node-fetch');
const fs = require('fs');

// Script saves file to current working directory
const url = '${url}';
const options = {
  method: '${endpoint.method}',
  headers: { 'Content-Type': 'application/json' }${isPost ? `,\n  body: JSON.stringify(${JSON.stringify(endpoint.payload)})` : ''}
};

fetch(url, options)
  .then(res => ${isBinary ? 'res.buffer()' : 'res.json()'})
  .then(data => {
    ${isBinary ? `// Save buffer to disk\n    fs.writeFileSync('${activeTab === 'pdf' ? 'mock.pdf' : 'mock.jpg'}', data);\n    console.log("Download Complete!");` : 'console.log(data);'}
  })
  .catch(err => console.error('error:' + err));`,

            'php': `<?php
// PHP cURL implementation
$curl = curl_init();
$url = "${url}";
${isPost ? `$payload = '${JSON.stringify(endpoint.payload)}';` : ''}

curl_setopt_array($curl, [
  CURLOPT_URL => $url,
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_CUSTOMREQUEST => "${endpoint.method}",
  ${isPost ? `CURLOPT_POSTFIELDS => $payload,` : ''}
  CURLOPT_HTTPHEADER => ["Content-Type: application/json"],
]);

$response = curl_exec($curl);
$err = curl_error($curl);
curl_close($curl);

if ($err) {
  echo "cURL Error #:" . $err;
} else {
  ${isBinary ? `// Save file to server\n  file_put_contents("${activeTab === 'pdf' ? 'mock.pdf' : 'mock.jpg'}", $response);\n  echo "Download Complete!";` : 'echo $response;'}
}
?>`,

            'curl': `# This command pulls the file and saves it to your current folder
curl -L -X ${endpoint.method} "${url}" -H "Content-Type: application/json"${isPost ? ` -d '${JSON.stringify(endpoint.payload)}'` : ''}${isBinary ? ` --output ${activeTab === 'pdf' ? 'mock.pdf' : 'mock.jpg'}` : ''}`
        };

        return snippets[lang] || '';
    };

    const runTest = async () => {
        setLoading(true);
        const endpoint = endpoints[activeTab];
        const url = getFullUrl();

        try {
            const options: any = {
                method: endpoint.method,
                headers: { 'Content-Type': 'application/json' }
            };
            if (endpoint.method === 'POST') {
                options.body = JSON.stringify(endpoint.payload);
            }

            const res = await fetch(url, options);

            if (endpoint.isBinary && res.status === 200) {
                const blob = await res.blob();
                const blobUrl = URL.createObjectURL(blob);
                setResponse({
                    status: res.status,
                    isBinary: true,
                    blobUrl,
                    fileName: activeTab === 'pdf' ? 'sample.pdf' : 'sample.jpg',
                    type: activeTab === 'pdf' ? 'application/pdf' : 'image/jpeg'
                });
            } else {
                const data = await res.json();
                setResponse({ status: res.status, body: data });
            }
        } catch (err: any) {
            setResponse({ status: 'Error', body: { message: err.message } });
        } finally {
            setLoading(false);
        }
    };


    // HTTP method badge color map (used in endpoint list)
    const methodColors: Record<string, string> = {
        GET: 'bg-[#e8f0fe] text-[#174ea6]',
        POST: 'bg-[#e6f4ea] text-[#0d652d]',
        PUT: 'bg-[#fef7e0] text-[#f9ab00]',
        DELETE: 'bg-[#fce8e6] text-[#d93025]',
    };

    return (
        /* .dev-service-page + .dev-container-clean + .dev-header-clean */
        <DevPageLayout
            title="Mock API Interactive Documentation"
            subtitle="Zero-config endpoints for testing, prototyping, and chaos exploration."
        >
            <SEO
                title="Mock API Service | FileFusion"
                description="A full-featured fake REST API for prototyping and testing. Features realistic data generation, chaos simulation, and binary file streaming."
            />

            <div className="grid grid-cols-1 gap-[32px] items-start lg:grid-cols-[320px_1fr]">

                {/* ── Docs Column ─────────────────────────────────── */}
                {/* .dev-panel sticky top-[72px] */}
                <DevPanel className="sticky top-[72px] h-auto mb-[40px] z-10">
                    {/* Panel header (custom, not mock-playground-header since it uses h2) */}
                    <div className="p-[12px_16px] border-b border-[#dadce0] flex justify-between items-center bg-[#f8f9fa]">
                        <h2 className="text-[14px] m-0 font-semibold text-[#202124]">Endpoints</h2>
                    </div>

                    {/* .dev-panel-body */}
                    <DevPanel.Body className="!p-[10px_10px_20px_10px]">
                        <div className="flex flex-col max-md:flex-wrap">
                            {(Object.keys(endpoints) as ActiveTab[]).map(key => (
                                <div key={key}>
                                    <button
                                        className={`w-full p-[4px_10px] bg-transparent border border-transparent rounded-[6px] justify-start text-left text-[13px] text-[#5f6368] cursor-pointer transition-all duration-200 flex items-center gap-[6px] whitespace-nowrap overflow-hidden text-ellipsis hover:bg-[#f8f9fa] hover:text-[#202124] ${activeTab === key ? '!bg-[#e8f0fe] !text-[#1a73e8] !border-[#e8f0fe] font-medium' : ''}`}
                                        onClick={() => { setActiveTab(key); setResponse(null); }}
                                    >
                                        <span className={`text-[9px] font-bold py-[2px] px-[4px] rounded-[4px] uppercase min-w-[36px] text-center ${methodColors[endpoints[key].method]}`}>
                                            {endpoints[key].method}
                                        </span>
                                        <span className="flex-1 text-left truncate">{endpoints[key].url}</span>
                                    </button>
                                    {activeTab === key && (
                                        <div className="p-[4px_10px_8px_10px] text-[11px] leading-[1.5] text-[#5f6368] flex gap-[8px]">
                                            <Info size={12} className="mt-[2px] flex-shrink-0 text-[#1a73e8]" />
                                            <span>{endpoints[key].description}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Chaos Parameters — .section-spacing → mt-[32px] */}
                        <div className="mt-[32px]">
                            <h2 className="text-[14px] font-semibold text-[#202124] mb-[12px] flex items-center gap-[8px]">
                                <Settings2 size={16} /> Chaos Parameters
                            </h2>

                            {/* .dev-control-row: mb-[8px] last:mb-0 */}
                            {/* .dev-control-row label: block text-[12px] font-semibold text-[#61666b] mb-[8px] uppercase */}
                            {/* .dev-control-row input[type=range]: w-full */}
                            {/* .dev-control-hint: text-[12px] text-[#54585d] */}
                            <div className="mb-[8px]">
                                <label htmlFor="sim-delay" className="block text-[12px] font-semibold text-[#61666b] mb-[8px] uppercase">
                                    Simulated Delay (ms)
                                </label>
                                <input
                                    id="sim-delay"
                                    type="range" min="0" max="5000" step="100"
                                    aria-label="Simulated Delay in milliseconds"
                                    value={delay}
                                    onChange={(e) => setDelay(Number(e.target.value))}
                                    className="w-full"
                                />
                                <span className="text-[12px] text-[#54585d]">{delay}ms</span>
                            </div>

                            <div className="mb-0">
                                <label htmlFor="status-override" className="block text-[12px] font-semibold text-[#61666b] mb-[8px] uppercase">
                                    Override Status Code
                                </label>
                                {/* .dev-control-row select / .dev-form-input:
                                    w-full px-[12px] py-[10px] border border-[#dadce0] rounded-[4px]
                                    text-[14px] bg-white transition-[border-color,box-shadow] duration-200
                                    focus:border-[#174ea6] focus:outline-none focus:shadow-[0_0_0_2px_#e8f0fe] */}
                                <select
                                    id="status-override"
                                    aria-label="HTTP Status Code Override"
                                    value={status}
                                    onChange={(e) => setStatus(Number(e.target.value))}
                                    className="w-full px-[12px] py-[10px] border border-[#dadce0] rounded-[4px] text-[14px] bg-white transition-[border-color,box-shadow] duration-200 focus:border-[#174ea6] focus:outline-none focus:shadow-[0_0_0_2px_#e8f0fe]"
                                >
                                    <option value="200">200 OK</option>
                                    <option value="201">201 Created</option>
                                    <option value="400">400 Bad Request</option>
                                    <option value="401">401 Unauthorized</option>
                                    <option value="403">403 Forbidden</option>
                                    <option value="404">404 Not Found</option>
                                    <option value="500">500 Server Error</option>
                                    <option value="503">503 Maintenance</option>
                                </select>
                            </div>
                        </div>
                    </DevPanel.Body>
                </DevPanel>

                {/* ── Playground Column ────────────────────────────── */}
                <div ref={playgroundRef} className="scroll-mt-[100px]">
                    <DevPanel>
                        {/* .mock-playground-header */}
                        <DevPanel.Header>
                            <h3>Interactive Playground</h3>
                            {/* .dev-btn-primary */}
                            <DevButton variant="primary" onClick={runTest} disabled={loading}>
                                {loading ? 'Running...' : <><Play size={16} /> Run Request</>}
                            </DevButton>
                        </DevPanel.Header>

                        {/* .dev-panel-body */}
                        <DevPanel.Body>

                            {/* 1. Response — .dev-window */}
                            <DevTerminalWindow
                                title="Response"
                                badge={response
                                    ? <DevStatusPill status={response.status === 200 || response.status === 201 ? 'success' : 'failed'}>
                                        {String(response.status)}
                                    </DevStatusPill>
                                    : null
                                }
                            >
                                {response ? (
                                    response.isBinary ? (
                                        <div className="text-center text-white">
                                            <FileCode size={48} className="text-[#1a73e8] mb-[16px] mx-auto" />
                                            <p className="m-[8px_0] text-[14px]">Binary File Received ({response.type})</p>
                                            {/* .dev-btn-primary used as anchor */}
                                            <a
                                                href={response.blobUrl}
                                                download={response.fileName}
                                                className="inline-flex items-center gap-[8px] px-[20px] py-[10px] bg-[#174ea6] text-white border-0 rounded-[4px] text-[14px] font-semibold cursor-pointer no-underline hover:bg-[#1557b0]"
                                            >
                                                Download {response.fileName}
                                            </a>
                                            {response.type?.startsWith('image/') && (
                                                <img src={response.blobUrl} alt="Preview" className="max-w-full mt-[20px] rounded-[4px] shadow-[0_4px_12px_rgba(0,0,0,0.5)]" />
                                            )}
                                        </div>
                                    ) : (
                                        <pre style={{ margin: 0, color: '#8ab4f8', fontSize: '13px', whiteSpace: 'pre-wrap' }}>
                                            {JSON.stringify(response.body, null, 2)}
                                        </pre>
                                    )
                                ) : (
                                    /* .dev-empty-state: text-center text-[#54585d] py-[40px] px-[20px] */
                                    /* svg inside: mb-[16px] opacity-50 */
                                    <div className="text-center text-[#54585d] py-[40px] px-[20px]">
                                        <Terminal size={48} className="mb-[16px] opacity-50 mx-auto" />
                                        <p>Click "Run Request" to see simulated data.</p>
                                    </div>
                                )}
                            </DevTerminalWindow>

                            {/* 2. Endpoint URL — .section-spacing → mt-[32px] */}
                            <div className="mt-[32px]">
                                <div className="flex justify-between items-center mb-[16px] max-md:flex-col max-md:items-start">
                                    <h2 className="text-[16px] flex items-center gap-[8px] m-0 font-semibold">
                                        <Link size={18} /> Endpoint URL
                                    </h2>
                                </div>

                                {/* .dev-url-preview */}
                                <DevUrlPreview method={endpoints[activeTab].method} url={getFullUrl()} />

                                {/* Request Body (if POST) */}
                                {endpoints[activeTab].payload && (
                                    <div className="mt-[32px]">
                                        <div className="flex justify-between items-center mb-[16px] max-md:flex-col max-md:items-start">
                                            <h2 className="text-[16px] flex items-center gap-[8px] m-0 font-semibold">
                                                <Braces size={18} /> Request Body
                                            </h2>
                                        </div>
                                        {/* .dev-code-block (no header, no copy) */}
                                        <DevCodeBlock className="!my-[8px]">
                                            <DevCodeBlockPre style={{ padding: '16px', fontSize: '13px' }}>
                                                {JSON.stringify(endpoints[activeTab].payload, null, 2)}
                                            </DevCodeBlockPre>
                                        </DevCodeBlock>
                                    </div>
                                )}
                            </div>

                            {/* 3. Client Implementation — .section-spacing → mt-[32px] */}
                            <div className="mt-[32px]">
                                <div className="flex justify-between items-center mb-[16px] max-md:flex-col max-md:items-start">
                                    <h2 className="text-[16px] flex items-center gap-[8px] m-0 font-semibold">
                                        <FileCode size={18} /> Client Implementation
                                    </h2>
                                    <div className="flex gap-[4px] flex-wrap max-md:mt-[12px]">
                                        {[
                                            { id: 'javascript-fetch', label: 'FETCH' },
                                            { id: 'javascript-axios', label: 'AXIOS' },
                                            { id: 'python-requests', label: 'PYTHON' },
                                            { id: 'node', label: 'NODE' },
                                            { id: 'php', label: 'PHP' },
                                            { id: 'curl', label: 'CURL' },
                                        ].map((tab: any) => (
                                            <button
                                                key={tab.id}
                                                className={`px-[12px] py-[6px] text-[11px] bg-white border border-[#dadce0] rounded-[6px] text-[#5f6368] cursor-pointer transition-all duration-200 uppercase font-bold tracking-[0.5px] leading-none hover:bg-[#f8f9fa] hover:text-[#202124] hover:border-[#80868b] ${snippetTab === tab.id ? '!bg-[#e8f0fe] !text-[#1a73e8] !border-[#1a73e8]' : ''}`}
                                                onClick={() => setSnippetTab(tab.id)}
                                            >
                                                {tab.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* .dev-code-block with header + copy */}
                                <DevCodeBlock
                                    label={<><FileCode size={14} /> {snippetTab.toUpperCase()} Snippet</>}
                                    copyText={generateSnippet(snippetTab)}
                                >
                                    <DevCodeBlockPre>{generateSnippet(snippetTab)}</DevCodeBlockPre>
                                </DevCodeBlock>

                                {/* Binary tip — .dev-alert .spacing-top → .dev-alert mt-[12px] */}
                                {endpoints[activeTab].isBinary && (
                                    <DevAlert icon={<Settings2 size={16} />} className="mt-[12px]">
                                        <p>
                                            <strong>Pro Tip:</strong> These snippets will save the file directly to your current folder as{' '}
                                            <code>{activeTab === 'pdf' ? 'mock.pdf' : 'mock.jpg'}</code>.
                                        </p>
                                    </DevAlert>
                                )}
                            </div>

                            {/* Footer Info — .dev-alert mt-[32px] with bg-white override */}
                            <div className="mt-[32px]">
                                <DevAlert icon={<AlertCircle size={18} className="!text-[#80868b]" />} className="!bg-white !border !border-[#dadce0]">
                                    <p className="!text-[#5f6368] !m-0">This service is stateless. Data is generated on-the-fly and never stored.</p>
                                </DevAlert>
                            </div>

                        </DevPanel.Body>
                    </DevPanel>
                </div>

            </div>
        </DevPageLayout>
    );
};

export default MockApiPage;
